import { useCallback, useEffect, useState } from "react";
import { FlagIcon } from "@phosphor-icons/react";
import { FlagDeficiencyDialog } from "./FlagDeficiencyDialog";

const TOOLBAR_OFFSET = 40;

type Props = {
  enabled: boolean;
  organizationId: number | null;
};

function selectionWithinFlaggableAnswer(selection: Selection): boolean {
  const node = selection.anchorNode;
  if (!node) return false;
  const element = node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement;
  return !!element?.closest("[data-flaggable-answer]");
}

export function ChatSelectionFlagAffordance({ enabled, organizationId }: Props) {
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null);
  const [quote, setQuote] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const syncToSelection = useCallback(() => {
    if (isDialogOpen) return;
    const selection = window.getSelection();
    const selectedText = selection?.toString().trim() ?? "";
    if (!selection || selection.rangeCount === 0 || !selectedText || !selectionWithinFlaggableAnswer(selection)) {
      setAnchor(null);
      return;
    }
    const rect = selection.getRangeAt(0).getBoundingClientRect();
    setQuote(selection.toString());
    setAnchor({ top: Math.max(rect.top - TOOLBAR_OFFSET, 8), left: Math.max(rect.left, 8) });
  }, [isDialogOpen]);

  useEffect(() => {
    if (!enabled) return;
    document.addEventListener("selectionchange", syncToSelection);
    window.addEventListener("scroll", syncToSelection, true);
    window.addEventListener("resize", syncToSelection);
    return () => {
      document.removeEventListener("selectionchange", syncToSelection);
      window.removeEventListener("scroll", syncToSelection, true);
      window.removeEventListener("resize", syncToSelection);
    };
  }, [enabled, syncToSelection]);

  if (!enabled || organizationId === null) return null;

  return (
    <>
      {anchor && !isDialogOpen && (
        <div
          className="fixed z-60 flex items-center gap-1 rounded-lg border border-innoveraZinc-150 bg-white px-1 py-1 shadow-lg"
          style={{ top: anchor.top, left: anchor.left }}
        >
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setIsDialogOpen(true)}
            className="flex items-center gap-1.5 rounded-md px-2 py-1 text-[13px] text-innoveraZinc-700 hover:bg-innoveraZinc-50"
          >
            <FlagIcon className="h-4 w-4" />
            Flag
          </button>
        </div>
      )}

      <FlagDeficiencyDialog
        isOpen={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        target={{ kind: "projectsListChat", organizationId }}
        quote={quote}
      />
    </>
  );
}
